import { motion } from 'framer-motion'
import { Rocket, Target, TrendingUp } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

const NextStepSection = () => {
  const goals = [
    {
      icon: Target,
      title: 'Become the OS',
      description: 'The definitive Operating System for industrial autonomy in Tunisia.',
      color: 'from-primary to-blue-600',
    },
    {
      icon: Rocket,
      title: 'Accelerate Manufacturing',
      description: 'Investment to scale production and meet growing regional demand.',
      color: 'from-orange-500 to-red-500',
    },
    {
      icon: TrendingUp,
      title: 'Finalize AI Datasets',
      description: 'Complete our proprietary AI models for unmatched optimization.',
      color: 'from-green-500 to-emerald-500',
    },
  ]

  return (
    <section className="py-20 bg-background relative overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/5" />
      
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-1/2 left-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>

      <div className="section-container relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6">
            The Next <span className="gradient-text">Step</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Our goal is to become the "Operating System" for Tunisia's autonomous industrial future
          </p>
        </motion.div>

        {/* Goals Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {goals.map((goal, index) => (
            <motion.div
              key={goal.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <Card className="h-full text-center hover:border-primary/30 transition-all duration-300">
                <CardContent className="p-8">
                  <div className={`w-20 h-20 bg-gradient-to-br ${goal.color} rounded-2xl flex items-center justify-center mx-auto mb-6`}>
                    <goal.icon className="text-white" size={40} />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-4">
                    {goal.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {goal.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Vision Statement */}
        <motion.div
          className="relative mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card className="overflow-hidden">
            <CardContent className="p-8 md:p-16 relative">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{
                  backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
                  backgroundSize: '30px 30px'
                }} />
              </div>

              <div className="relative z-10 text-center">
                <motion.div
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-primary to-accent rounded-full px-6 py-3 mb-8"
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <Rocket className="text-white" size={20} />
                  <span className="text-white font-semibold">Join the Revolution</span>
                </motion.div>

                <h3 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6 leading-tight">
                  Leading the Connected Intelligence
                  <br />
                  <span className="gradient-text">Revolution</span>
                </h3>

                <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-12 leading-relaxed">
                  "Join us in leading the Connected Intelligence revolution and shaping the 
                  future of Industry 4.0 in Tunisia."
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button size="lg" className="rounded-full" asChild>
                    <a href="#contact" className="inline-flex items-center space-x-2">
                      <span>Invest Now</span>
                      <Rocket size={20} />
                    </a>
                  </Button>
                  <Button size="lg" variant="outline" className="rounded-full" asChild>
                    <a href="tel:+21623486874">
                      +216 23 486 874
                    </a>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Future Vision Visual */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card>
            <CardContent className="p-8 md:p-12">
              <div className="text-center mb-8">
                <h3 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-4">
                  Vision 2030
                </h3>
                <p className="text-muted-foreground max-w-2xl mx-auto">
                  The future of industrial automation in the MENA region
                </p>
              </div>

              {/* Timeline Visualization */}
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-green-600 transform -translate-y-1/2" />
                
                {/* Timeline Points */}
                <div className="relative flex justify-between items-center">
                  {[
                    { year: '2024', label: 'Launch', status: 'completed' },
                    { year: '2025', label: 'Scale', status: 'current' },
                    { year: '2026', label: 'Expand', status: 'upcoming' },
                    { year: '2027+', label: 'Dominance', status: 'upcoming' },
                  ].map((point, index) => (
                    <motion.div
                      key={point.year}
                      className="flex flex-col items-center"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.15 }}
                    >
                      <motion.div
                        className={`w-4 h-4 rounded-full ${
                          point.status === 'completed'
                            ? 'bg-green-600'
                            : point.status === 'current'
                            ? 'bg-primary'
                            : 'bg-border'
                        }`}
                        animate={
                          point.status === 'current'
                            ? { scale: [1, 1.3, 1] }
                            : {}
                        }
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                        }}
                      />
                      <p className={`mt-4 font-semibold ${
                        point.status === 'current'
                          ? 'text-primary'
                          : point.status === 'completed'
                          ? 'text-green-600'
                          : 'text-muted-foreground'
                      }`}>
                        {point.year}
                      </p>
                      <p className="text-muted-foreground text-sm">{point.label}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  )
}

export default NextStepSection
